import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SecurityIcon from '@mui/icons-material/Security';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  useGetRolesQuery,
  useGetPermissionsQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useUpdateRolePermissionsMutation,
  useDeleteRoleMutation,
} from '../../../store/api/roleApi';

const MODULE_META = {
  dashboard: { label: 'Dashboard', icon: DashboardIcon, color: '#4ECDC4' },
  products: { label: 'Products', icon: InventoryIcon, color: '#FF6B6B' },
  inventory: { label: 'Inventory', icon: WarehouseIcon, color: '#45B7D1' },
  sales: { label: 'Sales', icon: ShoppingCartIcon, color: '#96CEB4' },
  purchases: { label: 'Purchases', icon: LocalShippingIcon, color: '#FFEAA7' },
  'van-sales': { label: 'Van Sales', icon: DirectionsCarIcon, color: '#DDA0DD' },
  expenses: { label: 'Expenses', icon: ReceiptLongIcon, color: '#FF8A65' },
  reports: { label: 'Reports', icon: AssessmentIcon, color: '#81C784' },
  finance: { label: 'Finance', icon: AccountBalanceIcon, color: '#64B5F6' },
  users: { label: 'Users', icon: PeopleIcon, color: '#BA68C8' },
  roles: { label: 'Roles', icon: AdminPanelSettingsIcon, color: '#4DB6AC' },
  settings: { label: 'Settings', icon: SettingsIcon, color: '#90A4AE' },
};

const ACTION_LABELS = { read: 'View', create: 'Create', update: 'Edit', delete: 'Delete', insufficient_stock: 'Sell Insufficient Stock', wholesale_sale: 'Wholesale Sales' };
const ACTION_COLORS = { read: '#4ECDC4', create: '#66BB6A', update: '#FFA726', delete: '#EF5350', insufficient_stock: '#9C27B0', wholesale_sale: '#6C5CE7' };

function RolesList() {
  const { data: roles, isLoading, error } = useGetRolesQuery();
  const { data: permissions } = useGetPermissionsQuery();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [updateRolePermissions] = useUpdateRolePermissionsMutation();
  const [deleteRole] = useDeleteRoleMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permRole, setPermRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', code: '', description: '' });
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const moduleGroups = useMemo(() => {
    const groups = {};
    (permissions || []).forEach((p) => {
      if (!groups[p.module]) {
        groups[p.module] = { resources: {} };
      }
      if (!groups[p.module].resources[p.resource]) {
        groups[p.module].resources[p.resource] = [];
      }
      groups[p.module].resources[p.resource].push(p);
    });
    return groups;
  }, [permissions]);

  const openCreate = () => {
    setEditingRole(null);
    setRoleForm({ name: '', code: '', description: '' });
    setSubmitError(null);
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, code: role.code, description: role.description || '' });
    setSubmitError(null);
    setDialogOpen(true);
  };

  const openDeleteConfirm = (role) => {
    setRoleToDelete(role);
    setDeleteError(null);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    try {
      setDeleteError(null);
      await deleteRole(roleToDelete.id).unwrap();
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
    } catch (err) {
      setDeleteError(err?.data?.message || 'Error deleting role');
    }
  };

  const handleRoleSave = async () => {
    try {
      setSubmitError(null);
      if (editingRole) {
        await updateRole({ id: editingRole.id, ...roleForm }).unwrap();
      } else {
        await createRole(roleForm).unwrap();
      }
      setDialogOpen(false);
      setEditingRole(null);
    } catch (err) {
      setSubmitError(err?.data?.message || 'Error saving role');
    }
  };

  const openPerms = (role) => {
    setPermRole(role);
    setSelectedPerms((role.permissions || []).map((p) => p.id));
    const expanded = {};
    Object.keys(moduleGroups).forEach((m) => { expanded[m] = true; });
    setExpandedModules(expanded);
    setPermDialogOpen(true);
  };

  const togglePerm = (id) => {
    setSelectedPerms((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleModule = (moduleName) => {
    const allPermsInModule = [];
    Object.values(moduleGroups[moduleName]?.resources || {}).forEach((perms) => {
      perms.forEach((p) => allPermsInModule.push(p.id));
    });
    const allSelected = allPermsInModule.every((id) => selectedPerms.includes(id));
    if (allSelected) {
      setSelectedPerms((prev) => prev.filter((id) => !allPermsInModule.includes(id)));
    } else {
      setSelectedPerms((prev) => [...new Set([...prev, ...allPermsInModule])]);
    }
  };

  const toggleResource = (moduleName, resource) => {
    const resourcePerms = (moduleGroups[moduleName]?.resources[resource] || []).map((p) => p.id);
    const allSelected = resourcePerms.every((id) => selectedPerms.includes(id));
    if (allSelected) {
      setSelectedPerms((prev) => prev.filter((id) => !resourcePerms.includes(id)));
    } else {
      setSelectedPerms((prev) => [...new Set([...prev, ...resourcePerms])]);
    }
  };

  const getModuleStatus = (moduleName) => {
    const allPermsInModule = [];
    Object.values(moduleGroups[moduleName]?.resources || {}).forEach((perms) => {
      perms.forEach((p) => allPermsInModule.push(p.id));
    });
    const selectedCount = allPermsInModule.filter((id) => selectedPerms.includes(id)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === allPermsInModule.length) return 'all';
    return 'partial';
  };

  const getResourceStatus = (moduleName, resource) => {
    const resourcePerms = (moduleGroups[moduleName]?.resources[resource] || []).map((p) => p.id);
    const selectedCount = resourcePerms.filter((id) => selectedPerms.includes(id)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === resourcePerms.length) return 'all';
    return 'partial';
  };

  const savePerms = async () => {
    if (!permRole) return;
    setSaving(true);
    try {
      await updateRolePermissions({ id: permRole.id, permissionIds: selectedPerms }).unwrap();
      setPermDialogOpen(false);
      setPermRole(null);
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const toggleExpand = (moduleName) => {
    setExpandedModules((prev) => ({ ...prev, [moduleName]: !prev[moduleName] }));
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading roles</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Roles & Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage user roles and control access to modules and actions
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 2 }}>
          Add Role
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles?.length ? (
                  roles.map((role) => {
                    const permCount = (role.permissions || []).length;
                    const totalPerms = (permissions || []).length;
                    return (
                      <TableRow key={role.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SecurityIcon sx={{ color: role.isSystem ? '#4ECDC4' : '#90A4AE', fontSize: 20 }} />
                            <Typography sx={{ fontWeight: 500 }}>{role.name}</Typography>
                            {role.isSystem && (
                              <Chip label="System" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 1, py: 0.25, borderRadius: 1, display: 'inline-block' }}>
                            {role.code}
                          </Typography>
                        </TableCell>
                        <TableCell>{role.description || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={permCount === totalPerms ? 'Full Access' : `${permCount} / ${totalPerms}`}
                              size="small"
                              color={permCount === totalPerms ? 'success' : permCount > 0 ? 'warning' : 'default'}
                              sx={{ fontWeight: 600, minWidth: 80 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit Role">
                            <IconButton size="small" color="primary" onClick={() => openEdit(role)} disabled={role.isSystem}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!role.isSystem && (
                            <Tooltip title="Delete Role">
                              <IconButton size="small" color="error" onClick={() => openDeleteConfirm(role)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Button size="small" variant="outlined" sx={{ ml: 1, borderRadius: 1.5 }} onClick={() => openPerms(role)} startIcon={<SecurityIcon />}>
                            Permissions
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No roles found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Role Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingRole ? 'Edit Role' : 'Add Role'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField label="Name" value={roleForm.name} onChange={(e) => setRoleForm((p) => ({ ...p, name: e.target.value }))} fullWidth />
            <TextField label="Code" value={roleForm.code} onChange={(e) => setRoleForm((p) => ({ ...p, code: e.target.value }))} fullWidth helperText="Unique identifier (e.g. manager, cashier, warehouse-staff)" />
            <TextField label="Description" value={roleForm.description} onChange={(e) => setRoleForm((p) => ({ ...p, description: e.target.value }))} fullWidth multiline rows={2} />
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
              <Button variant="outlined" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleRoleSave}>Save</Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog - Toggle Matrix */}
      <Dialog open={permDialogOpen} onClose={() => setPermDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 12px 40px rgba(0,0,0,0.15)', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon sx={{ color: '#4ECDC4' }} />
            Permissions for {permRole?.name}
          </Box>
          <Chip label={`${selectedPerms.length} selected`} size="small" color="primary" />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {Object.entries(moduleGroups).map(([moduleName, moduleData]) => {
              const meta = MODULE_META[moduleName] || { label: moduleName, icon: SettingsIcon, color: '#90A4AE' };
              const ModuleIcon = meta.icon;
              const moduleStatus = getModuleStatus(moduleName);
              const isExpanded = expandedModules[moduleName];

              return (
                <Paper key={moduleName} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', borderColor: moduleStatus === 'all' ? 'success.light' : moduleStatus === 'partial' ? 'warning.light' : 'divider' }}>
                  {/* Module header */}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', bgcolor: moduleStatus === 'all' ? 'rgba(76,175,80,0.04)' : moduleStatus === 'partial' ? 'rgba(255,152,0,0.04)' : 'transparent', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}
                    onClick={() => toggleExpand(moduleName)}
                  >
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: meta.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5 }}>
                      <ModuleIcon sx={{ fontSize: 20, color: meta.color }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600, flex: 1 }}>{meta.label}</Typography>
                    <Tooltip title={moduleStatus === 'all' ? 'Revoke all' : 'Grant all'}>
                      <Switch
                        size="small"
                        checked={moduleStatus === 'all'}
                        color={moduleStatus === 'all' ? 'success' : 'default'}
                        onClick={(e) => { e.stopPropagation(); toggleModule(moduleName); }}
                        sx={{ mr: 1 }}
                      />
                    </Tooltip>
                    {moduleStatus === 'all' && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, mr: 1 }} />}
                    {moduleStatus === 'partial' && <RemoveCircleOutlineIcon sx={{ color: 'warning.main', fontSize: 20, mr: 1 }} />}
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>

                  {/* Resource / Action rows */}
                  <Collapse in={isExpanded}>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5 }}>
                      {Object.entries(moduleData.resources).map(([resource, perms]) => {
                        const resStatus = getResourceStatus(moduleName, resource);
                        return (
                          <Box key={resource} sx={{ display: 'flex', alignItems: 'center', py: 0.75, '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' } }}>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Checkbox
                                size="small"
                                checked={resStatus === 'all'}
                                indeterminate={resStatus === 'partial'}
                                onChange={() => toggleResource(moduleName, resource)}
                                sx={{ p: 0.5 }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize', minWidth: 120 }}>
                                {resource.replace(/-/g, ' ')}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                              {perms.map((p) => {
                                const isSelected = selectedPerms.includes(p.id);
                                const actionColor = ACTION_COLORS[p.action];
                                return (
                                  <Chip
                                    key={p.id}
                                    label={ACTION_LABELS[p.action] || p.action}
                                    size="small"
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    onClick={() => togglePerm(p.id)}
                                    sx={{
                                      cursor: 'pointer',
                                      fontWeight: 600,
                                      fontSize: '0.72rem',
                                      height: 26,
                                      bgcolor: isSelected ? actionColor : 'transparent',
                                      color: isSelected ? '#fff' : actionColor,
                                      borderColor: actionColor,
                                      '&:hover': { bgcolor: isSelected ? actionColor : actionColor + '18' },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, pb: 1 }}>
              <Button variant="outlined" onClick={() => setPermDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={savePerms} disabled={saving}>
                {saving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Role?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to delete the role <strong>{roleToDelete?.name}</strong> ({roleToDelete?.code})? This action cannot be undone.
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteRole}>Delete</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default RolesList;
